/**
 * CareflowTab — Subtab container for the Careflow section of Wellness Settings.
 *
 * Renders a pp-subtab-toggle segmented control to switch between the 5 careflow
 * types (Alert, Device, Resident, Area, Default Emergency) and an "Output Settings"
 * button that opens the GlobalOutputDrawer. Each subtab renders a CareflowTable
 * with the appropriate filtered data.
 */

import { useMemo } from 'react';
import CareflowTable from './CareflowTable.jsx';

// ---------------------------------------------------------------------------
// Subtab definitions
// ---------------------------------------------------------------------------
const SUBTABS = [
    { key: 'alert', label: 'Alert', escTypeId: 1, isDefault: false },
    { key: 'device', label: 'Device', escTypeId: 3, isDefault: false },
    { key: 'resident', label: 'Resident', escTypeId: 4, isDefault: false },
    { key: 'area', label: 'Area', escTypeId: 5, isDefault: false },
    { key: 'default', label: 'Default Emergency', escTypeId: 1, isDefault: true },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CareflowTab = ({
    escData,
    eventCategories,
    campusSettings,
    activeSubTab,
    onSubTabChange,
    devices,
    mobileGroups,
    residentGroups,
    areaGroups,
    canManageCareflow = true,
    onOpenGlobalSettings,
    onAdd,
    onEdit,
    onDelete,
    onAssignDevices,
    onAssignResidentGroups,
    onAssignAreaGroups,
    onAssignMobileGroups,
    onAssignPagerGroups,
    onAssignTextPhones,
    onAssignVoicePhones,
    onAssignEmails,
    onSetOffsiteDelay,
    saving,
}) => {

    // Count careflows per subtab for badge display
    const counts = useMemo(() => {
        const c = { alert: 0, device: 0, resident: 0, area: 0, default: 0 };
        if (!escData?.length) return c;

        for (let i = 0; i < escData.length; i++) {
            const row = escData[i];
            if (row.escalation_type_id === 1 && row.event_category_id !== 1) {
                c.alert++;
            } else if (row.escalation_type_id === 3) {
                c.device++;
            } else if (row.escalation_type_id === 4) {
                c.resident++;
            } else if (row.escalation_type_id === 5) {
                c.area++;
            } else if (row.escalation_type_id === 1 && row.event_category_id === 1) {
                c.default++;
            }
        }
        return c;
    }, [escData]);

    // Filter data for active subtab
    const filteredData = useMemo(() => {
        if (!escData?.length) return [];

        switch (activeSubTab) {
            case 'alert':
                return escData.filter((r) => r.escalation_type_id === 1 && r.event_category_id !== 1);
            case 'device':
                return escData.filter((r) => r.escalation_type_id === 3);
            case 'resident':
                return escData.filter((r) => r.escalation_type_id === 4);
            case 'area':
                return escData.filter((r) => r.escalation_type_id === 5);
            case 'default':
                return escData.filter((r) => r.escalation_type_id === 1 && r.event_category_id === 1);
            default:
                return [];
        }
    }, [escData, activeSubTab]);

    return (
        <>
            {/* Header row — Output Settings button + subtab toggle */}
            <div className="ws-careflow-header">
                <div className="pp-subtab-toggle">
                    {SUBTABS.map((tab) => (
                        <button
                            key={tab.key}
                            className={`pp-subtab-btn${activeSubTab === tab.key ? ' is-active' : ''}`}
                            onClick={() => onSubTabChange(tab.key)}
                        >
                            {tab.label}
                            <span className="ws-subtab-badge">{counts[tab.key]}</span>
                        </button>
                    ))}
                </div>

                {canManageCareflow ? (
                    <button
                        className="ap-btn ap-btn--secondary"
                        onClick={onOpenGlobalSettings}
                        title="Configure global output settings for this campus"
                    >
                        <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" style={{ marginRight: '0.375rem' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                        Output Settings
                    </button>
                ) : null}
            </div>

            {/* Active subtab content */}
            <CareflowTable
                data={filteredData}
                subTabKey={activeSubTab}
                eventCategories={eventCategories}
                campusSettings={campusSettings}
                residentGroups={residentGroups}
                areaGroups={areaGroups}
                devices={devices}
                mobileGroups={mobileGroups}
                canManageCareflow={canManageCareflow}
                onAdd={() => onAdd(activeSubTab)}
                onEdit={(row) => onEdit(row, activeSubTab)}
                onDelete={onDelete}
                onAssignDevices={onAssignDevices}
                onAssignResidentGroups={onAssignResidentGroups}
                onAssignAreaGroups={onAssignAreaGroups}
                onAssignMobileGroups={onAssignMobileGroups}
                onAssignPagerGroups={onAssignPagerGroups}
                onAssignTextPhones={onAssignTextPhones}
                onAssignVoicePhones={onAssignVoicePhones}
                onAssignEmails={onAssignEmails}
                onSetOffsiteDelay={onSetOffsiteDelay}
                saving={saving}
            />
        </>
    );
};

export default CareflowTab;
